import { OnlineOrderItemFragment } from '@client/graphql/types/graphql.ts';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import AlertError from '@packages/ui/alert/alert-error.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import getImageLink from '@packages/core/image-source/get-image-link.ts';
import { useTranslate } from '@tolgee/react';
import { useCallback, useState } from 'react';

export default function OnlineOrderConfirmProofDialog(props: {
  order: Pick<
    OnlineOrderItemFragment,
    'manualTransferProofImageOrigin' | 'manualTransferProofImageThumbnail' | 'manualTransferSenderName'
  >;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}) {
  const { order, onConfirm, onCancel } = props;
  const { t } = useTranslate();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleConfirm = useCallback(() => {
    setLoading(true);
    onConfirm()
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err instanceof Error) {
          setError(err.message);
        }
      });
  }, [onConfirm]);

  const proofImage = order.manualTransferProofImageThumbnail ?? order.manualTransferProofImageOrigin;

  return (
    <div className="pt-5 px-8 pb-4 sm:my-2 sm:p-6">
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
            {t('online-order.confirm-proof.title', 'Confirm payment proof?')}
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              {t(
                'online-order.confirm-proof.message',
                'Make sure you have verified the payment proof against your bank app before accepting this order.',
              )}
            </p>
          </div>

          {order.manualTransferSenderName && (
            <p className="mt-3 text-sm">
              <span className="text-gray-500">
                {t('online-order.manual-transfer.panel.sender', 'Sender')}:{' '}
              </span>
              <span className="font-medium">{order.manualTransferSenderName}</span>
            </p>
          )}

          {proofImage && (
            <div className="mt-3">
              <img
                src={getImageLink(proofImage)}
                alt="Payment proof"
                className="max-h-72 max-w-full rounded-lg border border-gray-200 object-contain"
              />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4">
          <AlertError title="Error" message={t(error)} />
        </div>
      )}

      <div className="mt-5 sm:mt-4 flex flex-row-reverse">
        <PrimaryButton data-testid="confirm-proof" onClick={handleConfirm} loading={loading}>
          {t('online-order.confirm-proof.button', 'Yes, accept order')}
        </PrimaryButton>
        <div className="mr-4">
          <SecondaryButton disabled={loading} onClick={onCancel}>
            {t('common.button.cancel', 'Cancel')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
